import React, { FunctionComponent, useEffect, useState } from 'react'

import styled, { useTheme } from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { VIDEO_PLATFORM_DISPLAY_NAME } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/appointmentModalityConstants'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import {
  CheckboxButtonType,
  RadioButtonType,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Checkbox } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import {
  CheckboxGroup,
  CheckBoxGroupValue,
} from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/CheckboxGroup'
import { RadioGroup } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/RadioButtons'
import { InlineInfoBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineInfoBanner/InlineInfoBanner'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { actions as mixpanelActions, mixpanelEvents } from '../../../mixpanel/mixpanelConstants'
import { track } from '../../../mixpanel/mixpanelTracking'
import { useTimeZone } from '../calendar/hooks/useTimeZone'

type UpdateUpcomingSessionsModalityModalProps = {
  visible: boolean
  sessions: Appointment[]
  oldPlatform: VIDEO_PLATFORM
  newPlatform: VIDEO_PLATFORM
  onRequestClose: () => void
  onUpdateSessions: (appointmentIds: number[]) => void
  onUpdateAvailability: () => void
  onSkip: () => void
}

const ScrollContainer = styled.ScrollView({
  maxHeight: 'clamp(100px, calc(100vh - 450px), 500px)',
})

const ContentContainer = styled.View(({ theme }) => ({
  gap: theme.spacing['16px'],
}))

const Divider = styled.View(({ theme }) => ({
  height: '1px',
  backgroundColor: theme.colors.borderDefault,
}))

const SessionLabelRow = styled.View({
  flexDirection: 'row',
  flex: 1,
})

const SessionDateTime = styled(BodyText)({
  width: '150px',
  flexShrink: 0,
})

const SessionClientName = styled(BodyText)({
  flex: 1,
})

const formatSessionDateTime = (session: Appointment, providerTimeZone?: string): string => {
  const date = getDateFromAppointment(session)
  const timeZone = providerTimeZone ?? session.timeZone
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone })
  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone })
  return `${dateStr}, ${timeStr}`
}

const formatSessionClientName = (session: Appointment): string =>
  session.userInfo ? `${session.userInfo.firstName} ${session.userInfo.lastName}` : 'Client'

const SESSION_UPDATE_OPTION = {
  KEEP: 'keep',
  UPDATE: 'update',
} as const

type SessionUpdateOption = (typeof SESSION_UPDATE_OPTION)[keyof typeof SESSION_UPDATE_OPTION]

export const UpdateUpcomingSessionsModalityModal: FunctionComponent<UpdateUpcomingSessionsModalityModalProps> = ({
  visible,
  sessions,
  oldPlatform,
  newPlatform,
  onRequestClose,
  onUpdateSessions,
  onUpdateAvailability,
  onSkip,
}) => {
  const { colors } = useTheme()
  const providerTimeZone = useTimeZone()
  const allSessionIds = sessions.map((s) => String(s.appointmentId))
  const [selectedIds, setSelectedIds] = useState<string[]>(allSessionIds)
  const [sessionUpdateOption, setSessionUpdateOption] = useState<SessionUpdateOption>(SESSION_UPDATE_OPTION.UPDATE)

  useEffect(() => {
    if (visible) {
      setSelectedIds(sessions.map((s) => String(s.appointmentId)))
      setSessionUpdateOption(SESSION_UPDATE_OPTION.UPDATE)
    }
  }, [visible, sessions])

  const isAllSelected = allSessionIds.every((id) => selectedIds.includes(id))

  const handleSelectAllPress = () => {
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: mixpanelActions.VIDEO_SESSION_PREFERENCE_APPLY_TO_UPCOMING_SESSIONS_SELECT_ALL,
      details: { selected: isAllSelected ? 'unselected' : 'selected' },
    })
    setSelectedIds(isAllSelected ? [] : allSessionIds)
  }

  const selectedAppointmentIds = selectedIds.map((id) => parseInt(id, 10))

  const sessionOptions = sessions.map((session) => ({
    id: String(session.appointmentId),
    text: (
      <SessionLabelRow>
        <SessionDateTime
          testID={tID('UpdateUpcomingSessionsModalityModal-sessionDateTime')}
          text={formatSessionDateTime(session, providerTimeZone)}
        />
        <SessionClientName
          testID={tID('UpdateUpcomingSessionsModalityModal-sessionClientName')}
          text={formatSessionClientName(session)}
        />
      </SessionLabelRow>
    ),
  }))

  return (
    <ConfirmationModal
      testID={tID('UpdateUpcomingSessionsModalityModal')}
      visible={visible}
      onRequestClose={onRequestClose}
      modalTitle='Apply to upcoming sessions'
      modalContents={
        <ContentContainer>
          <BodyText
            text={`All availability with ${VIDEO_PLATFORM_DISPLAY_NAME[oldPlatform]} will automatically be updated to ${VIDEO_PLATFORM_DISPLAY_NAME[newPlatform]}. Should we also update your upcoming sessions?`}
          />
          <RadioGroup
            name='session-update-option'
            selectedValue={sessionUpdateOption}
            onChange={(val) => {
              const option = val as SessionUpdateOption
              track({
                event: mixpanelEvents.BUTTON_PRESS,
                action:
                  option === SESSION_UPDATE_OPTION.KEEP
                    ? mixpanelActions.VIDEO_SESSION_PREFERENCE_APPLY_TO_UPCOMING_SESSIONS_KEEP_SELECTED
                    : mixpanelActions.VIDEO_SESSION_PREFERENCE_APPLY_TO_UPCOMING_SESSIONS_UPDATE_SELECTED,
              })
              setSessionUpdateOption(option)
            }}
            buttons={[
              { label: 'Keep existing sessions as-is', value: SESSION_UPDATE_OPTION.KEEP },
              { label: 'Update selected sessions', value: SESSION_UPDATE_OPTION.UPDATE },
            ]}
            buttonType={RadioButtonType.DESCRIPTION_NO_OUTLINE}
            baseInputStyle={{ marginBottom: 0 }}
          />
          {sessionUpdateOption === SESSION_UPDATE_OPTION.UPDATE && (
            <>
              <InlineInfoBanner
                text="If it's less than 72 hours before the session time, we'll email your clients to notify them of this change."
                backgroundColor={colors.backgroundSection}
                textColor={colors.textSecondary}
                iconColor={colors.textSecondary}
                iconSize={24}
                testId={tID('UpdateUpcomingSessionsModalityModal-infoBanner')}
                marginBottom={'0px'}
              />
              <Checkbox
                name='upcoming-sessions-select-all'
                title={`Select all (${sessions.length})`}
                checked={isAllSelected}
                onPress={handleSelectAllPress}
                buttonType={CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE}
                baseInputStyle={{ marginBottom: 0 }}
              />
              <Divider />
              <ScrollContainer testID={tID('UpdateUpcomingSessionsModalityModal-sessionsContainer')}>
                <CheckboxGroup
                  name='upcoming-sessions'
                  value={selectedIds}
                  onChange={(val: CheckBoxGroupValue) => setSelectedIds(val as string[])}
                  options={sessionOptions}
                  buttonType={CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE}
                  checkboxInputStyle={{ marginBottom: 8 }}
                />
              </ScrollContainer>
            </>
          )}
        </ContentContainer>
      }
      confirmationButtonText='Update'
      cancelButtonText='Cancel'
      onConfirmationButtonPress={() =>
        sessionUpdateOption === SESSION_UPDATE_OPTION.UPDATE
          ? onUpdateSessions(selectedAppointmentIds)
          : onUpdateAvailability()
      }
      onCancelPressed={onSkip}
      confirmButtonDisabled={
        sessionUpdateOption === SESSION_UPDATE_OPTION.UPDATE && selectedAppointmentIds.length === 0
      }
    />
  )
}
